/**
 * Takes image urls, and brings up a print dialog with one page per image. Each image is the size of the page.
 *
 * Written for certificates, adapted from examples in
 * https://blakeelearning.atlassian.net/wiki/spaces/K/pages/2038333576/Notes+for+Parent+and+Teacher+teams+consuming+new+RE+certificates
 */
export async function printImagesFitToPage(imageUrls: string[], documentTitle?: string) {
  /* Create img html string, rather than document.createElement('img') or an Image class to avoid loading the image once
   * here, then once again in the iframe.
   * Apply max width and height to ensure that regardless of paper size or orientation, each cert uses exactly 1 page.
   */
  const imagesHTML = imageUrls
    .map((imageUrl) => `<img src="${imageUrl}" style="width: 100%; height: 100%; max-width: 98vw; max-height: 98vh;">`)
    .join('')

  /*
   * Bug #1 - Safari 14 and 15 when printed from an iframe show blank pages. Fixed in 16. (but see Bug #3)
   * Bug #2 - Similarly, those 2 versions also don't reliably tell us when the print dialog is closed. Specifically
   * referring to a dialog that MacOS Safari shows, that appears before the "print preview" dialog, if the user asks to
   * print a second time. If the user cancels that pre-print-preview dialog, the print prep has been done (eg iframe has
   * been appended) but no print events are fired to trigger the cleanup.
   * Note: This seems fixed in Safari 16.4+
   * Note: Not an issue with new window, as it will not show the pre-print-preview dialog in a new window.
   * See:
   * https://github.com/crabbly/Print.js/issues/528
   * https://github.com/gregnb/react-to-print/issues/397
   * (and heaps of other issues online around this pre-print-preview dialog)
   * Bug #3 - Safari (latest tested 16.5) seems to not resize an iframe to match the size of the print window. In an
   * iframe, the width/height's based on viewport dimentions (vw/vh) point to the size of the iframe. This means that
   * in safari, the iframe is the wrong size and therefore the images inside are also the wrong size, and do not appear.
   *
   * For these reasons, rather than playing version whackamole, Safari opens a new window for print.
   *
   * Bug #4 - Android browsers fire afterprint way too early, before the print preview, regardless of iframe vs window.
   * Which means the cleanup occurs too early, which causes different problems depending on the approach used;
   * - iframe cleaned up too early; print preview shows the parent page
   * - new window closed too early; the print preview fails
   *
   * Technically, it actually fires it each time it generates a print, rather than "after" the user has printed. So
   * if you change the print settings (eg from A4 -> Letter) it fires again. This is a completely different way of
   * handling print to every other browser. This means you cannot just "delay" the cleanup by an amount of time, as you
   * cannot know when the user might change the print settings.
   *
   * For this reason, Android browsers open a new window for print, ***and do not close the opened window.***
   */
  const isSafari = navigator.userAgent.match(/Version\/[0-9]+\.[0-9]+(\.[0-9]+)? Safari/) // desktop or ios
  const isiOSChrome = navigator.userAgent.includes('CriOS') && navigator.userAgent.includes('Safari')
  const isAndroid = navigator.userAgent.includes('Android')
  const printAsNewWindow = isSafari || isiOSChrome || isAndroid

  if (printAsNewWindow) {
    const popup = window.open()
    if (popup) {
      // desktop safari seems to ignore the vw/vh units in the new window for print
      if (isSafari) {
        popup.document.body.style.width = '98%'
        popup.document.body.style.height = '98%'
      }
      popup.document.body.innerHTML = imagesHTML
      if (documentTitle) popup.document.title = documentTitle // used as part of pdf filename

      await waitForDocumentImages(popup.document)

      popup.print()
      if (!isAndroid) popup.close()
    }
  } else {
    // Create a new iframe
    const hiddenIframe = createPrintIframe()

    /* Set up a print promise that resolves when the iframe is loaded, and images are loaded. This is to ensure this
     * function can be awaited if its a big job.
     */
    const printPromise = new Promise((resolvePrint, _rejectPrint) => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      hiddenIframe.addEventListener('load', async () => {
        // after iframe & its images loaded - open print dialog (and ideally remove iframe after print..)
        if (hiddenIframe.contentWindow && hiddenIframe.contentDocument) {
          // used as part of pdf filename - only sometimes works when printing from an iframe, depends on browser
          if (documentTitle) hiddenIframe.contentDocument.title = documentTitle

          // This is only needed for safari. All other browsers' iframe onload is fired after images are loaded.
          // However we can leave this here to be damn sure the images are loaded.
          await waitForDocumentImages(hiddenIframe.contentDocument)

          hiddenIframe.contentWindow.addEventListener('afterprint', cleanupPrintIframe)
          hiddenIframe.contentWindow.print()
        }
        resolvePrint(true)
      })
    })

    /* Need to apply the images as html to the iframe srcdoc so that they are included in the load, and there is no
     * document.body to write or append elements to until the iframe is appended.
     */
    hiddenIframe.srcdoc = imagesHTML

    document.body.appendChild(hiddenIframe)

    // Only return from this function when complete - allowing the caller to show a spinner, disable a button etc.
    await printPromise
  }
}

async function waitForDocumentImages(htmlDocument: Document) {
  const imageElements = Array.from(htmlDocument.querySelectorAll('img') ?? [])
  if (imageElements.some((imgEl) => !imgEl.complete)) {
    const loadPromises = imageElements
      .filter((imgEl) => !imgEl.complete)
      .map((imgEl) => {
        return new Promise((resolve, reject) => {
          imgEl.onload = () => resolve(imgEl)
          imgEl.onerror = (e) => reject(new Error(typeof e === 'string' ? e : 'Error loading image for print'))
        })
      })
    await Promise.all(loadPromises)
  }
}

const iframeId = 'print-iframe'

function createPrintIframe() {
  const hiddenIframe = document.createElement('iframe')
  hiddenIframe.id = iframeId
  hiddenIframe.classList.add('hidden')
  hiddenIframe.classList.add('print:block')
  return hiddenIframe
}

function cleanupPrintIframe() {
  const existingIframe = document.body.querySelector(`iframe#${iframeId}`)
  if (existingIframe) document.body.removeChild(existingIframe)
}
